import { ScrollArea } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Images, Library, User } from "lucide-react";
import { useState } from "react";

import {
  getListJobsQueryKey,
  useGetCatalog,
} from "../../api/generated/endpoints/generation/generation";
import type { AiActorGender } from "../../api/generated/model";
import { ActorsComposer } from "../../platform/actors/composer/ActorsComposer";
import { PageColumns } from "../../ui/PageColumns";
import { TypeTabs } from "../../ui/TypeTabs";
import {
  GlobalActors,
  NO_ACTORS_QUERY,
  type ActorsQuery,
} from "../../platform/actors/library/GlobalActors";
import { ActorGenerations } from "./ActorGenerations";
import { MyActors } from "../../platform/actors/library/MyActors";
import classes from "../../platform/actors/library/actors.module.css";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/actors";

/**
 * Live's AI Actors page (`/app/actors-ai`, captures `design-ref/ai-actors/`, spec SA4): My
 * Actors (the default, the team's own, #221), Global Actors (live's 119 actors, #225) and My
 * Generations (the brand's actor generations), under live's floating composer (Generate With Text
 * now; the Builder and Upload Actor next). The search, the order and the facets are the page's,
 * kept across the tabs, as live's.
 */
export function ActorsScreen() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const search = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: "/teams/$teamId/brands/$brandId/actors" });
  const view = search.view ?? "mine";
  const catalog = useGetCatalog().data;
  const queryClient = useQueryClient();
  const show = (next: string) =>
    void navigate({
      search: { view: next === "mine" ? undefined : (next as "global" | "generations") },
      replace: true,
    });
  const [query, setQuery] = useState<ActorsQuery>(NO_ACTORS_QUERY);
  const [gender, setGender] = useState<AiActorGender | undefined>(undefined);
  // My Actors' creator chips (live's `filter-by-user`), kept while another tab shows, as Gender.
  const [creators, setCreators] = useState<string[]>([]);
  // Live's Product, one at a time, kept while another tab shows, as Gender (#237).
  const [product, setProduct] = useState<string | undefined>(undefined);
  const onQuery = (change: Partial<ActorsQuery>) => setQuery((current) => ({ ...current, ...change }));
  return (
    <PageColumns
      scrollable={false}
      bottom={
        catalog === undefined ? null : (
          <ActorsComposer
            teamId={teamId}
            brandId={brandId}
            catalog={catalog}
            onStarted={() => {
              void queryClient.invalidateQueries({ queryKey: getListJobsQueryKey(teamId) });
              // Live shows My Generations once a generation starts.
              show("generations");
            }}
          />
        )
      }
      top={
        <TypeTabs
          label="AI Actors"
          value={view}
          onChange={show}
          tabs={[
            { value: "mine", label: "My Actors", icon: <User size={16} /> },
            { value: "global", label: "Global Actors", icon: <Library size={16} /> },
            { value: "generations", label: "My Generations", icon: <Images size={16} /> },
          ]}
        />
      }
    >
      {view === "global" ? (
        <GlobalActors
          teamId={teamId}
          brandId={brandId}
          query={query}
          onQuery={onQuery}
        />
      ) : view === "generations" ? (
        <ScrollArea type="auto" offsetScrollbars className={classes.fill}>
          <ActorGenerations teamId={teamId} brandId={brandId} catalog={catalog} />
        </ScrollArea>
      ) : (
        <MyActors
          teamId={teamId}
          brandId={brandId}
          query={query}
          onQuery={onQuery}
          gender={gender}
          onGender={setGender}
          creators={creators}
          onCreators={setCreators}
          product={product}
          onProduct={setProduct}
        />
      )}
    </PageColumns>
  );
}
